import React, { useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { Priority } from 'app/PopupHolder';
import PropertyDetailPopup from 'app/Property/Detail/Detail';
import Loader from 'components/Loader';
import SVG from 'components/base/SVG';

import { useToast } from 'app/components/ToastMessage/ToastManager';
import { exportInvestorPropertiesCsv } from 'data/investors/investorProfileService';

import css from './style.scss';

// Linked Properties / Related Real Estate Agents — Figma "tab set"
// (545:35828) + Table, property1="property" (36:3366), inside the Investor
// Profile card.
//
// Figma's tab set has a third tab, "Properties They Might Be Interested"
// (545:35832) — but it's marked `hidden` on the design itself, so it isn't
// built here either.
//
// "Linked Properties" is now real: the union of `acquisitionProperties` and
// `dispositionProperties` — both split by Profile/index.js's
// useInvestorProfileData from one `ownership=any` call to ATAI-169's
// `GET /ps-core/investors/{id}/properties` — every property PropStream has
// ever linked to this investor, current or sold, in one list. Rows PropStream
// can't resolve are left out server-side (ATAI-472 — they used to show as
// blank lines). The "N linked properties" download line shows the same
// Portfolio Size the MetricGrid above it shows (`investor.metrics
// .portfolioSize`), so the two numbers on one profile never disagree; it
// falls back to `propertiesTotal` (the vendor's list total) and then to
// `properties.length` while the profile metrics aren't in hand.
// "Related Real Estate Agents" has no dedicated endpoint — each row's
// own `relatedAgent` (agentName/agentPhone/agentEmail/officeName/officePhone)
// is the only source, so that tab lists the deduplicated agents found across
// the same properties instead of a separate fetch.
//
// Figma's header row repeats "State" as both the 3rd and 8th column, the
// latter showing a date-shaped placeholder ("00/00/00") with no
// corresponding property field — dropped rather than kept as an always-blank
// column.
const TABS = [
  { key: 'OWNED', label: 'Linked Properties' },
  { key: 'AGENTS', label: 'Related Real Estate Agents' },
];

export const COLUMNS = ['Address', 'City', 'State', 'Zip', 'County', 'APN'];

export const AGENT_COLUMNS = ['Name', 'Phone', 'Email', 'Office Name', 'Office Phone'];

// Blank, not a dash — a missing value here (an address' county, an agent's
// office phone) just renders as empty space, not a placeholder character.
export const EMPTY = '';

// One row per distinct agent across all of this investor's properties —
// keyed by email (falling back to name+phone when a row has no email) since
// the same agent commonly shows up on more than one property.
const hasAnyAgentData = (agent) =>
  Boolean(agent.agentName || agent.agentPhone || agent.agentEmail || agent.officeName || agent.officePhone);

const dedupeAgents = (properties) => {
  const byKey = new Map();
  properties.forEach(({ relatedAgent }) => {
    if (!relatedAgent || !hasAnyAgentData(relatedAgent)) return;
    const key = relatedAgent.agentEmail || `${relatedAgent.agentName}-${relatedAgent.agentPhone}`;
    if (!byKey.has(key)) byKey.set(key, relatedAgent);
  });
  return [...byKey.values()];
};

// Shared with ./History, whose Acquisition/Disposition tabs list the same
// `InvestorPropertyRow` shape (address/city/state/zip/county/apn — a flat
// PropStream-sourced row, not the nested `{address: {...}}` shape this table
// assumed before ATAI-169 existed to source it from).
//
// The address opens Property Details as its own stacked popup — NOT a
// `<Link>`/route navigation. This row lives inside the already-open Investor
// Profile popup, and a route change just navigates the page underneath
// while the Investor Profile modal stays on top of it (confirmed: that's
// exactly what a plain `Link` did here). `components/Map/PropertyInfo`
// solves this identical "clicked from inside another popup" problem the
// same way: dispatch `PropertyDetailPopup.open` with `preventRouting: true`,
// which renders Property Details as its own overlay and skips the
// history-push `closeDetailPopup` would otherwise do on close. Uses
// PropStream's own `propertyId` — not `batchDataPropertyId`, which Property
// Details doesn't know how to look up.
//
// `priority: Priority.HIGHEST` is set only at this call site, the same way
// `EditContactModal.open` sets it — PopupHolder is a per-priority queue, and
// opening Property Details at the Investor Profile modal's own priority
// (MEDIUM) left it queued invisibly behind it instead of stacking on top.
// HIGHEST also gets the "return to the investor modal on close" behavior for
// free: PopupHolder just reveals whatever's next in the queue once the front
// popup closes, so Investor Profile reappears without this row needing to
// close/reopen it manually. This is scoped to opening Property Details from
// inside the Investor Profile modal specifically — every other caller of
// `PropertyDetailPopup.open` (e.g. `components/Map/PropertyInfo`) doesn't
// pass a priority and is unaffected.
//
// `priority` alone wasn't enough, though: PopupHolder's own queue-level
// z-index gap (a plain 10-per-level difference) turned out not to matter,
// because SimpleModal (the Investor Profile modal itself) hardcodes its own
// `z-index: 9999` in its stylesheet, while Property Details' `<Modal>`
// defaults to 999 — two independent stacking contexts a small queue-level
// z-index difference can't reach across. `zIndex` (added to `Detail.js`,
// forwarded from an otherwise-unused prop — every other caller still gets
// the unchanged 999 default) is what actually wins that fight.
// `onPropertyDetailToggle` (threaded down from Profile/index.js) tells the
// Investor Profile modal to stop treating a click as "outside itself" while
// Property Details is open — see that file's own comment on
// `suppressOutsideClick` for why a plain z-index/priority fix isn't enough
// on its own: SimpleModal's outside-click listener can't tell Property
// Details' own DOM subtree apart from anything else outside its ref, so a
// click that closes Property Details (outside *its* content) also reads as
// outside the Investor Profile modal and would close both at once.
export const PropertyRow = ({ property, rank, onPropertyDetailToggle }) => {
  const dispatch = useDispatch();

  const openDetails = () => {
    onPropertyDetailToggle?.(true);
    dispatch(
      PropertyDetailPopup.open({
        propertyId: property.propertyId,
        preventRouting: true,
        match: { params: { id: property.propertyId } },
        priority: Priority.HIGHEST,
        zIndex: 10000, // above SimpleModal's hardcoded 9999
        onClose: () => onPropertyDetailToggle?.(false),
      })
    );
  };

  return (
    <div className={classNames(css.tableRow, { [css['tableRow--alt']]: rank % 2 === 0 })}>
      <span className={css.tableCell__rank}>{rank}</span>
      <span className={classNames(css.tableCell, css.tableCell__address)}>
        <span className={css.tableCell__text}>
          {property.address && property.propertyId ? (
            <button type="button" onClick={openDetails}>
              {property.address}
            </button>
          ) : (
            property.address || EMPTY
          )}
        </span>
      </span>
      <span className={css.tableCell}>
        <span className={css.tableCell__text}>{property.city || EMPTY}</span>
      </span>
      <span className={css.tableCell}>
        <span className={css.tableCell__text}>{property.state || EMPTY}</span>
      </span>
      <span className={css.tableCell}>
        <span className={css.tableCell__text}>{property.zip || EMPTY}</span>
      </span>
      <span className={css.tableCell}>
        <span className={css.tableCell__text}>{property.county || EMPTY}</span>
      </span>
      <span className={css.tableCell}>
        <span className={css.tableCell__text}>{property.apn || EMPTY}</span>
      </span>
    </div>
  );
};

const AgentRow = ({ agent, rank }) => (
  <div
    className={classNames(css.tableRow, css['tableRow--agents'], {
      [css['tableRow--alt']]: rank % 2 === 0,
    })}
  >
    <span className={css.tableCell__rank}>{rank}</span>
    <span className={css.tableCell}>
      <span className={css.tableCell__text}>{agent.agentName || EMPTY}</span>
    </span>
    <span className={css.tableCell}>
      <span className={css.tableCell__text}>{agent.agentPhone || EMPTY}</span>
    </span>
    <span className={css.tableCell}>
      <span className={css.tableCell__text}>{agent.agentEmail || EMPTY}</span>
    </span>
    <span className={css.tableCell}>
      <span className={css.tableCell__text}>{agent.officeName || EMPTY}</span>
    </span>
    <span className={css.tableCell}>
      <span className={css.tableCell__text}>{agent.officePhone || EMPTY}</span>
    </span>
  </div>
);

const PropertyTable = ({
  investor,
  acquisitionProperties = [],
  dispositionProperties = [],
  propertiesTotal,
  propertiesLoading = false,
  onPropertyDetailToggle,
}) => {
  const [tab, setTab] = useState(TABS[0].key);
  const [exporting, setExporting] = useState(false);
  const showToast = useToast();
  // Both PropStream ids differ per row (acquisitionProperties/
  // dispositionProperties never overlap for the same investor — a property
  // is either currently owned or previously sold, not both), so a plain
  // concat is enough; no de-dupe needed.
  const properties = [...acquisitionProperties, ...dispositionProperties];
  const totalCount = investor?.metrics?.portfolioSize ?? propertiesTotal ?? properties.length;
  const agents = dedupeAgents(properties);

  // The CSV comes from the service now, not from what happens to be rendered.
  // The table caps at ten rows, so building the file client-side could only ever
  // export that first page; GET .../properties/export returns the investor's
  // whole owned list, which is what the copy below has always promised.
  const handleDownloadCsv = async () => {
    if (exporting) return;
    setExporting(true);

    try {
      await exportInvestorPropertiesCsv(investor.id || investor._id);
    } catch (error) {
      console.error('Could not export the property list:', error);
      // Without this the click looks like it simply did nothing — the download
      // is the only feedback a success gives, so a failure needs its own.
      showToast?.('Could not download the CSV. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={css.section}>
      <div className={css.tabs}>
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={classNames(css.tab, { [css.tabActive]: tab === key })}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'OWNED' && propertiesLoading && (
        <div className={css.emptyState}>
          <Loader loaderSize="small" />
        </div>
      )}
      {tab === 'OWNED' && !propertiesLoading && !properties.length && (
        <div className={css.emptyState}>
          <p>No linked properties on record for this investor yet.</p>
        </div>
      )}
      {tab === 'OWNED' && !propertiesLoading && properties.length > 0 && (
        <div className={css.table}>
          <div className={css.tableRow}>
            <span className={css.tableCell__rank}>#</span>
            {COLUMNS.map((label, index) => (
              // Both "State" columns render here — see the header comment
              // above for why the second one is kept despite the duplicate
              // label.
              <span key={`${label}-${index}`} className={css.tableCell}>
                <span className={css.tableCell__text}>{label}</span>
              </span>
            ))}
          </div>
          {properties.map((property, index) => (
            <PropertyRow
              key={property.propertyId || property.batchDataPropertyId || index}
              property={property}
              rank={index + 1}
              onPropertyDetailToggle={onPropertyDetailToggle}
            />
          ))}
          {/* ATAI-372: Figma's Zip/County divider is one continuous line the
              full height of the table, not a border repeated per row -- see
              .tableColumnDivider's own comment in style.scss. Rendered last
              (not first) so it doesn't take the header row's `:first-child`
              spot -- `position: absolute` keeps it out of the flex flow
              either way, so where it sits in the DOM doesn't affect layout,
              only which sibling counts as "first". */}
          <span className={css.tableColumnDivider} aria-hidden="true" />
        </div>
      )}
      {tab === 'AGENTS' && propertiesLoading && (
        <div className={css.emptyState}>
          <Loader loaderSize="small" />
        </div>
      )}
      {tab === 'AGENTS' && !propertiesLoading && !agents.length && (
        <div className={css.emptyState}>
          <p>No related real estate agents on record for this investor yet.</p>
        </div>
      )}
      {tab === 'AGENTS' && !propertiesLoading && agents.length > 0 && (
        <div className={css.table}>
          <div className={classNames(css.tableRow, css['tableRow--agents'])}>
            <span className={css.tableCell__rank}>#</span>
            {AGENT_COLUMNS.map((label, index) => (
              <span key={`${label}-${index}`} className={css.tableCell}>
                <span className={css.tableCell__text}>{label}</span>
              </span>
            ))}
          </div>
          {agents.map((agent, index) => (
            <AgentRow key={agent.agentEmail || `${agent.agentName}-${index}`} agent={agent} rank={index + 1} />
          ))}
        </div>
      )}

      {tab === 'OWNED' && properties.length > 0 && (
        <div className={css.downloadRow}>
          <p className={css.downloadRow__copy}>
            There {totalCount === 1 ? 'is' : 'are'} {totalCount} linked{' '}
            {totalCount === 1 ? 'property' : 'properties'}.{' '}
            {/* True as written since the export moved server-side: the table
                shows the first ten, the file carries the lot. No trailing
                period, per Figma (ATAI-372). */}
            {'Download the CSV to view them all'}
          </p>
          <button
            type="button"
            className={css.editButton}
            disabled={exporting}
            onClick={handleDownloadCsv}
          >
            {exporting ? <SVG icon="spinner" className={css.buttonSpinner} /> : 'Download CSV File'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PropertyTable;
